// Словарь авиакомпаний: канонические названия + алиасы (включая IATA-коды).
// Чистый модуль, без внешних зависимостей.

export interface CarrierDef {
  canonical: string;
  /** алиасы в нижнем регистре: разговорные названия и IATA-коды */
  aliases: string[];
}

export const CARRIER_LIST: readonly CarrierDef[] = [
  { canonical: 'Wizz Air Malta', aliases: ['wizz air malta', 'wizz malta'] },
  { canonical: 'Wizz Air', aliases: ['wizz air', 'wizzair', 'wizz', 'w6'] },
  { canonical: 'Belavia', aliases: ['belavia', 'b2'] },
  { canonical: 'FlyOne', aliases: ['flyone', 'fly one', '5f'] },
  { canonical: 'Georgian Airways', aliases: ['georgian airways', 'georgian', 'a9'] },
  { canonical: 'Turkish Airlines', aliases: ['turkish airlines', 'turkish', 'thy', 'tk'] },
  { canonical: 'Ryanair', aliases: ['ryanair', 'fr'] },
  { canonical: 'easyJet', aliases: ['easyjet', 'easy jet', 'u2', 'ezy'] },
  { canonical: 'Aegean Airlines', aliases: ['aegean airlines', 'aegean', 'a3'] },
  { canonical: 'AJet', aliases: ['ajet', 'anadolujet'] },
  { canonical: 'Pegasus Airlines', aliases: ['pegasus airlines', 'pegasus', 'pc'] },
  { canonical: 'Air Baltic', aliases: ['air baltic', 'airbaltic', 'bt'] },
  { canonical: 'LOT Polish Airlines', aliases: ['lot polish airlines', 'lot', 'lo'] },
  { canonical: 'Lufthansa', aliases: ['lufthansa', 'lh'] },
  { canonical: 'KLM', aliases: ['klm'] },
  { canonical: 'Air France', aliases: ['air france', 'af'] },
  { canonical: 'Austrian Airlines', aliases: ['austrian airlines', 'austrian', 'os'] },
  { canonical: 'SWISS', aliases: ['swiss international air lines', 'swiss', 'lx'] },
  { canonical: 'Vueling', aliases: ['vueling', 'vy'] },
  { canonical: 'Qatar Airways', aliases: ['qatar airways', 'qatar', 'qr'] },
  { canonical: 'Emirates', aliases: ['emirates', 'ek'] },
  { canonical: 'flydubai', aliases: ['flydubai', 'fly dubai', 'fz'] },
  { canonical: 'Etihad Airways', aliases: ['etihad airways', 'etihad', 'ey'] },
  { canonical: 'Aeroflot', aliases: ['aeroflot', 'su'] },
  { canonical: 'S7 Airlines', aliases: ['s7 airlines', 's7'] },
  { canonical: 'Uzbekistan Airways', aliases: ['uzbekistan airways', 'uzbekistan', 'hy'] },
  { canonical: 'Air Astana', aliases: ['air astana', 'kc'] },
  { canonical: 'SCAT Airlines', aliases: ['scat airlines', 'scat', 'dv'] },
  { canonical: 'Azerbaijan Airlines', aliases: ['azerbaijan airlines', 'azal', 'j2'] },
  { canonical: 'Israir', aliases: ['israir', '6h'] },
  { canonical: 'El Al', aliases: ['el al', 'elal', 'ly'] },
  { canonical: 'Arkia', aliases: ['arkia', 'iz'] },
  { canonical: "Sun d'Or", aliases: ["sun d'or", 'sundor', 'sun dor'] },
  { canonical: 'Cyprus Airways', aliases: ['cyprus airways'] },
  { canonical: 'TUS Airways', aliases: ['tus airways', 'tus'] },
  { canonical: 'Norwegian', aliases: ['norwegian', 'dy'] },
  { canonical: 'SAS', aliases: ['scandinavian airlines', 'sas', 'sk'] },
  { canonical: 'Finnair', aliases: ['finnair', 'ay'] },
  { canonical: 'Iberia', aliases: ['iberia', 'ib'] },
  { canonical: 'TAP Air Portugal', aliases: ['tap air portugal', 'tap portugal', 'tap', 'tp'] },
];

export const CARRIERS: readonly string[] = CARRIER_LIST.map((c) => c.canonical);

const CYR_LAT_BOUND = 'A-Za-zА-Яа-яЁё0-9';

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function boundedTest(haystack: string, needle: string): boolean {
  const esc = escapeRegex(needle);
  const re = new RegExp(`(?<![${CYR_LAT_BOUND}])${esc}(?![${CYR_LAT_BOUND}])`, 'i');
  return re.test(haystack);
}

/**
 * Находит авиакомпанию по алиасу/коду в тексте. Регистронезависимо.
 * При нескольких совпадениях выигрывает более длинный алиас/название.
 */
export function matchCarrier(text: string): string {
  let best: { canonical: string; len: number } | null = null;
  for (const def of CARRIER_LIST) {
    const candidates = [def.canonical.toLowerCase(), ...def.aliases];
    for (const c of candidates) {
      if (c.length < 2) continue;
      if (boundedTest(text, c) && (!best || c.length > best.len)) {
        best = { canonical: def.canonical, len: c.length };
      }
    }
  }
  return best ? best.canonical : '';
}

/** Двухбуквенные коды авиакомпаний из словаря — используются при поиске номера рейса. */
export const TWO_LETTER_CARRIER_CODES: ReadonlySet<string> = new Set(
  CARRIER_LIST.flatMap((c) => c.aliases.filter((a) => /^[a-z0-9]{2}$/i.test(a)).map((a) => a.toUpperCase()))
);
